import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Type, Save, Download, Upload, Copy, X, RotateCcw, Eye, Globe } from 'lucide-react';
import GoogleTranslateService from '../services/googleTranslateService';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.div`
  padding: 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.background};
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleSwitch = styled.button`
  width: 40px;
  height: 20px;
  border-radius: 10px;
  border: none;
  background: ${props => props.active ? props.theme.primary : props.theme.border};
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: ${props => props.active ? '22px' : '2px'};
    transition: all 0.2s ease;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  color: ${props => props.theme.text};
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.text};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const RangeInput = styled.input`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: ${props => props.theme.border};
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.theme.primary};
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.theme.primary};
    cursor: pointer;
    border: none;
  }
`;

const RangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RangeValue = styled.span`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  min-width: 40px;
`;

const ColorInput = styled.input`
  width: 60px;
  height: 32px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  cursor: pointer;
  background: none;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
  }
`;

const PreviewContainer = styled.div`
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  overflow: hidden;
`;

const PreviewTitle = styled.div`
  padding: 8px 12px;
  background: ${props => props.theme.surface};
  border-bottom: 1px solid ${props => props.theme.border};
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.textSecondary};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const VideoPreview = styled.div`
  position: relative;
  height: 120px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-weight: 500;
`;

const SubtitleOverlay = styled.div`
  position: absolute;
  ${props => {
    // 🆕 Support custom X/Y positioning
    if (props.customPosition && props.customPosition.enabled) {
      return `
        left: ${props.customPosition.x}%;
        top: ${props.customPosition.y}%;
        transform: translate(-50%, -50%);
      `;
    }
    
    // Use predefined positions
    const [vertical, horizontal] = props.position.split('-');
    let styles = '';
    
    if (vertical === 'top') styles += 'top: 10px;';
    else if (vertical === 'bottom') styles += 'bottom: 10px;';
    else styles += 'top: 50%; transform: translateY(-50%);';
    
    if (horizontal === 'left') styles += 'left: 10px;';
    else if (horizontal === 'right') styles += 'right: 10px;';
    else styles += 'left: 50%; transform: translateX(-50%);';
    
    if (vertical === 'center' && horizontal === 'center') {
      styles += 'transform: translate(-50%, -50%);';
    }
    
    return styles;
  }}
  
  background: ${props => props.backgroundColor};
  color: ${props => props.fontColor};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: ${props => Math.max(8, props.fontSize * 0.6)}px;
  opacity: ${props => props.backgroundOpacity / 100};
  max-width: 200px;
  text-align: center;
  word-wrap: break-word;
  
  /* 🆕 Custom font support */
  font-family: ${props => {
    if (props.customFont && props.customFont !== 'Arial') {
      return `"${props.fontFamily}", Arial, sans-serif`;
    }
    return props.fontFamily || 'Arial';
  }};
  
  /* 🆕 Text shadow support */
  text-shadow: ${props => 
    props.textShadow && props.textShadow.enabled 
      ? `${props.textShadow.offsetX}px ${props.textShadow.offsetY}px ${props.textShadow.blur}px ${props.textShadow.color}`
      : 'none'
  };
  
  /* 🆕 Custom positioning indicator */
  ${props => props.customPosition && props.customPosition.enabled ? `
    &::before {
      content: '📍';
      position: absolute;
      top: -20px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 12px;
      opacity: 0.7;
    }
  ` : ''}
`;

const ResetButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.textSecondary};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.border};
  }
`;

const PresetActions = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid ${props => props.variant === 'primary' ? props.theme.primary : props.theme.border};
  border-radius: 6px;
  background: ${props => props.variant === 'primary' ? props.theme.primary : props.theme.surface};
  color: ${props => props.variant === 'primary' ? 'white' : props.theme.text};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'primary' ? props.theme.primary + 'dd' : props.theme.border};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SavedPresetsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PresetItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
`;

const PresetName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.text};
`;

const PresetInfo = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  margin-top: 2px;
`;

const PresetActions2 = styled.div`
  display: flex;
  gap: 4px;
`;

const SmallButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.border};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.background};
  border-radius: 8px;
  padding: 24px;
  width: 400px;
  max-width: 90vw;
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  color: ${props => props.theme.text};
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;
  margin-bottom: 16px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ModalFormGroup = styled.div`
  margin-bottom: 16px;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.text};
  margin-bottom: 6px;
`;

const SubtitleSettings = ({ project, onUpdate }) => {
  const [settings, setSettings] = useState({
    enabled: true,
    maxWordsPerLine: 8,
    fontSize: 36,  // ✅ Fixed: Use larger default font size for better visibility
    fontColor: '#ffffff',
    backgroundColor: '#000000',
    backgroundOpacity: 70,
    position: 'bottom-center',
    customPosition: {
      enabled: false,
      x: 50,
      y: 90
    },
    fontFamily: 'Arial',
    customFont: null,
    textShadow: {
      enabled: true,
      color: '#000000',
      blur: 2,
      offsetX: 1,
      offsetY: 1
    }
  });

  const [savedSubtitlePresets, setSavedSubtitlePresets] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [availableFonts, setAvailableFonts] = useState([]);
  const [showFontUpload, setShowFontUpload] = useState(false);

  useEffect(() => {
    // Load settings from project
    if (project?.settings?.subtitle) {
      setSettings(prev => ({ ...prev, ...project.settings.subtitle }));
    }

    // Load saved presets
    loadSubtitlePresets();

    // Load available languages based on project translations
    loadAvailableLanguages();
    
    // 🆕 Load available custom fonts
    loadAvailableFonts();
  }, [project, project?.translations, project?.transcriptionLanguage]);

  const loadSubtitlePresets = () => {
    try {
      const saved = localStorage.getItem('subtitlePresets');
      if (saved) {
        setSavedSubtitlePresets(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading subtitle presets:', error);
    }
  };

  const loadAvailableLanguages = () => {
    try {
      // Get languages from project translations + original language
      const projectLanguages = [];
      
      // Add original transcription language
      const originalLanguage = project?.transcriptionLanguage || 'en';
      const SUPPORTED_LANGUAGES = [
        { code: 'ar', name: 'العربية', flag: '🇸🇦' },
        { code: 'en', name: 'English', flag: '🇺🇸' },
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'es', name: 'Español', flag: '🇪🇸' },
        { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
        { code: 'it', name: 'Italiano', flag: '🇮🇹' },
        { code: 'pt', name: 'Português', flag: '🇵🇹' },
        { code: 'ru', name: 'Русский', flag: '🇷🇺' },
        { code: 'zh', name: '中文', flag: '🇨🇳' },
        { code: 'ja', name: '日本語', flag: '🇯🇵' }
      ];
      
      // Add original language first
      const originalLangInfo = SUPPORTED_LANGUAGES.find(lang => lang.code === originalLanguage);
      if (originalLangInfo) {
        projectLanguages.push({ code: originalLangInfo.code, name: originalLangInfo.name });
      }
      
      // Add translation languages from project
      const translations = project?.translations || {};
      Object.keys(translations).forEach(langCode => {
        if (translations[langCode] && translations[langCode].length > 0) {
          const langInfo = SUPPORTED_LANGUAGES.find(lang => lang.code === langCode);
          if (langInfo && !projectLanguages.find(l => l.code === langCode)) {
            projectLanguages.push({ code: langInfo.code, name: langInfo.name });
          }
        }
      });
      
      // If no translations yet, add common languages
      if (projectLanguages.length === 1) {
        const commonLanguages = ['ar', 'en', 'fr', 'es'];
        commonLanguages.forEach(langCode => {
          if (langCode !== originalLanguage) {
            const langInfo = SUPPORTED_LANGUAGES.find(lang => lang.code === langCode);
            if (langInfo && !projectLanguages.find(l => l.code === langCode)) {
              projectLanguages.push({ code: langInfo.code, name: langInfo.name });
            }
          }
        });
      }
      
      setAvailableLanguages(projectLanguages);
    } catch (error) {
      console.error('Error loading project languages:', error);
      // Fallback languages
      setAvailableLanguages([
        { code: 'ar', name: 'العربية' },
        { code: 'en', name: 'English' },
        { code: 'fr', name: 'Français' },
        { code: 'es', name: 'Español' }
      ]);
    }
  };

  // 🆕 NEW: Load custom fonts from fonts folder
  const loadAvailableFonts = async () => {
    try {
      const defaultFonts = [
        'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana',
        'Trebuchet MS', 'Impact', 'Comic Sans MS', 'Courier New', 'Lucida Console'
      ];
      
      // Load custom fonts - try multiple sources
      let customFonts = [];
      
      // First, try to load from localStorage (always available)
      try {
        const savedCustomFonts = JSON.parse(localStorage.getItem('customFonts') || '[]');
        customFonts = [...savedCustomFonts];
      } catch (error) {
        console.log('No saved custom fonts found');
      }
      
      // Second, try Electron if available
      try {
        if (window.require) {
          const { ipcRenderer } = window.require('electron');
          const fontsPath = 'public/fonts';
          const fontFiles = await ipcRenderer.invoke('list-directory', fontsPath);
          
          fontFiles.forEach(file => {
            if (file.endsWith('.ttf') || file.endsWith('.otf') || file.endsWith('.woff') || file.endsWith('.woff2')) {
              const fontName = file.replace(/\.[^/.]+$/, "");
              // Only add if not already in customFonts
              if (!customFonts.find(f => f.name === fontName)) {
                customFonts.push({ 
                  name: fontName, 
                  file: file, 
                  type: 'custom',
                  path: `fonts/${file}`
                });
              }
            }
          });
        }
      } catch (error) {
        console.log('Electron custom fonts not available, using fallback methods');
      }

      // Always ensure Bahij Arabic font is present if file exists in public/fonts
      const bahijName = 'Bahij TheSansArabic';
      const bahijFile = 'bahij-thesansarabic-bold.ttf';
      if (!customFonts.find(f => f.name === bahijName)) {
        customFonts.push({
          name: bahijName,
          file: bahijFile,
          type: 'custom',
          path: `fonts/${bahijFile}`
        });
      }
      
      setAvailableFonts([
        ...defaultFonts.map(font => ({ name: font, type: 'system' })),
        ...customFonts
      ]);
    } catch (error) {
      console.error('Error loading fonts:', error);
      // Fallback to default fonts
      const defaultFonts = [
        'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana'
      ];
      setAvailableFonts(defaultFonts.map(font => ({ name: font, type: 'system' })));
    }
  };

  // 🆕 NEW: Handle font file upload
  const handleFontUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['font/ttf', 'font/otf', 'application/font-woff', 'application/font-woff2'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(ttf|otf|woff|woff2)$/i)) {
      alert('⚠️ Only TTF, OTF, WOFF, and WOFF2 font files are supported');
      return;
    }

    try {
      if (window.require) {
        const { ipcRenderer } = window.require('electron');
        
        // Copy font file to public/fonts folder
        const reader = new FileReader();
        reader.onload = async (e) => {
          const fontData = e.target.result;
          const fontPath = `public/fonts/${file.name}`;
          
          await ipcRenderer.invoke('save-file', fontPath, fontData);
          console.log(`✅ Font uploaded: ${file.name}`);
          
          // Reload available fonts
          loadAvailableFonts();
          
          // Set the new font as selected
          const fontName = file.name.replace(/\.[^/.]+$/, "");
          updateSettings({ fontFamily: fontName, customFont: file.name });
          
          alert(`✅ Font "${fontName}" uploaded successfully!`);
        };
        reader.readAsArrayBuffer(file);
      } else {
        alert('Font upload not available in this environment');
      }
    } catch (error) {
      console.error('Error uploading font:', error);
      alert('❌ Failed to upload font. Please try again.');
    }
  };

  const updateSettings = (updates) => {
    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);
    
    console.log('🔄 SubtitleSettings: updateSettings called with:', updates);
    console.log('🔄 SubtitleSettings: newSettings will be:', newSettings);
    
    // Update project settings
    const updatedProject = {
      ...project,
      settings: {
        ...project.settings,
        subtitle: newSettings
      }
    };
    
    console.log('🔄 SubtitleSettings: Updated project subtitle settings:', updatedProject.settings.subtitle);
    console.log('🔄 SubtitleSettings: Calling onUpdate with project:', updatedProject.id);
    
    onUpdate(updatedProject);
  };

  const resetToDefaults = () => {
    const defaults = {
      enabled: true,
      maxWordsPerLine: 8,
      fontSize: 36,  // ✅ Fixed: Use larger default font size for better visibility
      fontColor: '#ffffff',
      backgroundColor: '#000000',
      backgroundOpacity: 70,
      position: 'bottom-center',
      customPosition: {
        enabled: false,
        x: 50,
        y: 90
      },
      fontFamily: 'Arial',
      customFont: null,
      textShadow: {
        enabled: true,
        color: '#000000',
        blur: 2,
        offsetX: 1,
        offsetY: 1
      }
    };
    setSettings(defaults);
    updateSettings(defaults);
  };

  const saveSubtitlePreset = () => {
    if (!presetName.trim()) return;
    
    // Use smart default language based on project
    const originalLang = project?.transcriptionLanguage || 'en';
    const defaultLang = originalLang === 'en' ? 'ar' : 'en';
    const presetLanguage = availableLanguages.find(l => l.code === defaultLang)?.code || availableLanguages[0]?.code || 'ar';
    
    const preset = {
      id: Date.now().toString(),
      name: presetName.trim(), // PRESERVE ORIGINAL NAME
      language: presetLanguage,
      maxWordsPerLine: settings.maxWordsPerLine,
      fontSize: settings.fontSize,
      fontColor: settings.fontColor,
      backgroundColor: settings.backgroundColor,
      backgroundOpacity: settings.backgroundOpacity,
      position: settings.position,
      // 🆕 ADVANCED FEATURES INCLUDED
      customPosition: settings.customPosition || {
        enabled: false,
        x: 50,
        y: 90
      },
      fontFamily: settings.fontFamily || 'Arial',
      customFont: settings.customFont || null,
      textShadow: settings.textShadow || {
        enabled: true,
        color: '#000000',
        blur: 2,
        offsetX: 1,
        offsetY: 1
      },
      createdAt: new Date().toISOString()
    };

    const updated = [...savedSubtitlePresets, preset];
    setSavedSubtitlePresets(updated);
    localStorage.setItem('subtitlePresets', JSON.stringify(updated));
    setShowSaveModal(false);
    setPresetName('');
  };

  const loadSubtitlePreset = (preset) => {
    console.log('📥 Loading preset:', preset.name, 'with settings:', preset);
    
    const presetSettings = {
      enabled: true,
      maxWordsPerLine: preset.maxWordsPerLine,
      fontSize: preset.fontSize,
      fontColor: preset.fontColor,
      backgroundColor: preset.backgroundColor,
      backgroundOpacity: preset.backgroundOpacity,
      position: preset.position,
      // 🆕 LOAD ADVANCED FEATURES FROM PRESET
      customPosition: preset.customPosition || {
        enabled: false,
        x: 50,
        y: 90
      },
      fontFamily: preset.fontFamily || 'Arial',
      customFont: preset.customFont || null,
      textShadow: preset.textShadow || {
        enabled: true,
        color: '#000000',
        blur: 2,
        offsetX: 1,
        offsetY: 1
      }
    };
    setSettings(presetSettings);
    updateSettings(presetSettings);
    
    console.log('✅ Applied preset WITH ADVANCED FEATURES:', presetSettings);
  };

  const deleteSubtitlePreset = (presetId) => {
    const updated = savedSubtitlePresets.filter(p => p.id !== presetId);
    setSavedSubtitlePresets(updated);
    localStorage.setItem('subtitlePresets', JSON.stringify(updated));
  };

  const getLanguageName = (code) => {
    const lang = availableLanguages.find(l => l.code === code);
    return lang ? lang.name : code;
  };

  const positions = [
    { value: 'top-left', label: 'Top Left' },
    { value: 'top-center', label: 'Top Center' },
    { value: 'top-right', label: 'Top Right' },
    { value: 'center-left', label: 'Center Left' },
    { value: 'center-center', label: 'Center' },
    { value: 'center-right', label: 'Center Right' },
    { value: 'bottom-left', label: 'Bottom Left' },
    { value: 'bottom-center', label: 'Bottom Center' },
    { value: 'bottom-right', label: 'Bottom Right' }
  ];

  return (
    <Container>
      <Section>
        <SectionTitle>
          <Type size={16} />
          Subtitle Settings
        </SectionTitle>
        
        <ToggleContainer>
          <ToggleSwitch
            active={settings.enabled}
            onClick={() => updateSettings({ enabled: !settings.enabled })}
          />
          <ToggleLabel>
            {settings.enabled ? 'Enabled' : 'Disabled'}
          </ToggleLabel>
        </ToggleContainer>

        {settings.enabled && (
          <div style={{ 
            padding: '12px', 
            background: '#f0f8ff', 
            borderRadius: '8px', 
            border: '2px dashed #6f42c1',
            marginBottom: '16px'
          }}>
            <div style={{ 
              fontSize: '13px', 
              fontWeight: '600', 
              color: '#6f42c1', 
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              📝 Subtitle Configuration
            </div>
            
            <div style={{ 
              fontSize: '11px', 
              color: '#6c757d',
              marginBottom: '12px',
              fontStyle: 'italic',
              padding: '6px 8px',
              backgroundColor: '#e7f3ff',
              borderRadius: '4px',
              border: '1px solid #b3d9ff'
            }}>
              💡 Language selection is available in Parts Cutter → Subtitles button
            </div>
            
            <FormGroup>
              <Label>📝 Max Words Per Line</Label>
              <RangeContainer>
                <RangeInput
                  type="range"
                  min="1"
                  max="15"
                  value={settings.maxWordsPerLine}
                  onChange={(e) => updateSettings({ maxWordsPerLine: parseInt(e.target.value) })}
                />
                <RangeValue>{settings.maxWordsPerLine} words</RangeValue>
              </RangeContainer>
            </FormGroup>

            <FormGroup>
              <Label>📏 Font Size</Label>
              <RangeContainer>
                <RangeInput
                  type="range"
                  min="12"
                  max="72"
                  value={settings.fontSize}
                  onChange={(e) => updateSettings({ fontSize: parseInt(e.target.value) })}
                />
                <RangeValue>{settings.fontSize}px</RangeValue>
              </RangeContainer>
            </FormGroup>

            <FormGroup>
              <Label>🎨 Font Color</Label>
              <ColorInput
                type="color"
                value={settings.fontColor}
                onChange={(e) => updateSettings({ fontColor: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>🎨 Background Color</Label>
              <ColorInput
                type="color"
                value={settings.backgroundColor}
                onChange={(e) => updateSettings({ backgroundColor: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>🔍 Background Opacity</Label>
              <RangeContainer>
                <RangeInput
                  type="range"
                  min="0"
                  max="100"
                  value={settings.backgroundOpacity}
                  onChange={(e) => updateSettings({ backgroundOpacity: parseInt(e.target.value) })}
                />
                <RangeValue>{settings.backgroundOpacity}%</RangeValue>
              </RangeContainer>
            </FormGroup>

            <FormGroup>
              <Label>📍 Position</Label>
              <Select
                value={settings.position}
                onChange={(e) => updateSettings({ position: e.target.value })}
                disabled={settings.customPosition?.enabled}
              >
                {positions.map(pos => (
                  <option key={pos.value} value={pos.value}>
                    {pos.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            {/* 🆕 NEW: Custom Position with X/Y coordinates */}
            <FormGroup>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="checkbox"
                  checked={settings.customPosition?.enabled || false}
                  onChange={(e) => updateSettings({
                    customPosition: {
                      ...settings.customPosition,
                      enabled: e.target.checked
                    }
                  })}
                />
                <Label>🎯 Custom Position (X/Y)</Label>
              </div>
              
              {settings.customPosition?.enabled && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <Label style={{ fontSize: '12px', color: '#666' }}>X Position (%) - Safe Range</Label>
                    <RangeContainer>
                      <RangeInput
                        type="range"
                        min="5"
                        max="95"
                        value={settings.customPosition?.x || 50}
                        onChange={(e) => updateSettings({
                          customPosition: {
                            ...settings.customPosition,
                            x: parseInt(e.target.value)
                          }
                        })}
                      />
                      <RangeValue>{settings.customPosition?.x || 50}%</RangeValue>
                    </RangeContainer>
                    <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                      🛡️ Safe range: 5%-95% (prevents text from going off-screen)
                    </div>
                  </div>
                  
                  <div>
                    <Label style={{ fontSize: '12px', color: '#666' }}>Y Position (%) - Safe Range</Label>
                    <RangeContainer>
                      <RangeInput
                        type="range"
                        min="10"
                        max="90"
                        value={settings.customPosition?.y || 50}
                        onChange={(e) => updateSettings({
                          customPosition: {
                            ...settings.customPosition,
                            y: parseInt(e.target.value)
                          }
                        })}
                      />
                      <RangeValue>{settings.customPosition?.y || 50}%</RangeValue>
                    </RangeContainer>
                    <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                      🛡️ Safe range: 10%-90% (prevents text from going off-screen)
                    </div>
                  </div>
                </div>
              )}
            </FormGroup>

            {/* 🆕 NEW: Font Family with Custom Fonts */}
            <FormGroup>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <Label>🔤 Font Family</Label>
                <button
                  type="button"
                  onClick={() => setShowFontUpload(!showFontUpload)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '11px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    background: '#f8f9fa',
                    cursor: 'pointer'
                  }}
                >
                  📁 Add Font
                </button>
              </div>
              
              <Select
                value={settings.fontFamily || 'Arial'}
                onChange={(e) => {
                  const selectedFont = availableFonts.find(f => f.name === e.target.value);
                  updateSettings({ 
                    fontFamily: e.target.value,
                    customFont: selectedFont?.file || null
                  });
                }}
              >
                {availableFonts.map(font => (
                  <option key={font.name} value={font.name}>
                    {font.name} {font.type === 'custom' ? '🎨' : ''}
                  </option>
                ))}
              </Select>
              
              {showFontUpload && (
                <div style={{ marginTop: '8px', padding: '12px', border: '1px dashed #ddd', borderRadius: '6px' }}>
                  <Label style={{ fontSize: '12px', marginBottom: '6px' }}>Upload Custom Font</Label>
                  <input
                    type="file"
                    accept=".ttf,.otf,.woff,.woff2"
                    onChange={handleFontUpload}
                    style={{ fontSize: '12px', width: '100%' }}
                  />
                  <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                    Supported formats: TTF, OTF, WOFF, WOFF2
                  </div>
                </div>
              )}
            </FormGroup>

            {/* 🆕 NEW: Text Shadow Settings */}
            <FormGroup>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="checkbox"
                  checked={settings.textShadow?.enabled || false}
                  onChange={(e) => updateSettings({
                    textShadow: {
                      ...settings.textShadow,
                      enabled: e.target.checked
                    }
                  })}
                />
                <Label>✨ Text Shadow</Label>
              </div>
              
              {settings.textShadow?.enabled && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <Label style={{ fontSize: '11px' }}>Color</Label>
                    <ColorInput
                      type="color"
                      value={settings.textShadow?.color || '#000000'}
                      onChange={(e) => updateSettings({
                        textShadow: {
                          ...settings.textShadow,
                          color: e.target.value
                        }
                      })}
                    />
                  </div>
                  
                  <div>
                    <Label style={{ fontSize: '11px' }}>Blur</Label>
                    <RangeContainer>
                      <RangeInput
                        type="range"
                        min="0"
                        max="10"
                        value={settings.textShadow?.blur || 2}
                        onChange={(e) => updateSettings({
                          textShadow: {
                            ...settings.textShadow,
                            blur: parseInt(e.target.value)
                          }
                        })}
                      />
                      <RangeValue>{settings.textShadow?.blur || 2}px</RangeValue>
                    </RangeContainer>
                  </div>
                  
                  <div>
                    <Label style={{ fontSize: '11px' }}>Offset X</Label>
                    <RangeContainer>
                      <RangeInput
                        type="range"
                        min="-5"
                        max="5"
                        value={settings.textShadow?.offsetX || 1}
                        onChange={(e) => updateSettings({
                          textShadow: {
                            ...settings.textShadow,
                            offsetX: parseInt(e.target.value)
                          }
                        })}
                      />
                      <RangeValue>{settings.textShadow?.offsetX || 1}px</RangeValue>
                    </RangeContainer>
                  </div>
                  
                  <div>
                    <Label style={{ fontSize: '11px' }}>Offset Y</Label>
                    <RangeContainer>
                      <RangeInput
                        type="range"
                        min="-5"
                        max="5"
                        value={settings.textShadow?.offsetY || 1}
                        onChange={(e) => updateSettings({
                          textShadow: {
                            ...settings.textShadow,
                            offsetY: parseInt(e.target.value)
                          }
                        })}
                      />
                      <RangeValue>{settings.textShadow?.offsetY || 1}px</RangeValue>
                    </RangeContainer>
                  </div>
                </div>
              )}
            </FormGroup>
          </div>
        )}
      </Section>

      {settings.enabled && (
        <Section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <SectionTitle>Preview</SectionTitle>
            <ResetButton onClick={resetToDefaults}>
              <RotateCcw />
              Reset
            </ResetButton>
          </div>
          
          <PreviewContainer>
            <PreviewTitle>
              <Eye size={14} />
              Subtitle Preview
            </PreviewTitle>
            <VideoPreview>
              Video Content
              {settings.enabled && (
                <SubtitleOverlay
                  position={settings.position}
                  fontSize={settings.fontSize}
                  fontColor={settings.fontColor}
                  backgroundColor={settings.backgroundColor}
                  backgroundOpacity={settings.backgroundOpacity}
                  customPosition={settings.customPosition}
                  fontFamily={settings.fontFamily}
                  textShadow={settings.textShadow}
                >
                  Sample subtitle text
                </SubtitleOverlay>
              )}
            </VideoPreview>
          </PreviewContainer>
        </Section>
      )}

      {/* Subtitle Presets Section */}
      <Section>
        <SectionTitle>
          <Save size={16} />
          Subtitle Presets
        </SectionTitle>
        
        <PresetActions>
          <ActionButton
            variant="primary"
            onClick={() => setShowSaveModal(true)}
            disabled={!settings.enabled}
          >
            <Save size={14} />
            Save Current Subtitle
          </ActionButton>
          
          <ActionButton onClick={() => document.getElementById('subtitlePresetUpload').click()}>
            <Upload size={14} />
            Import Presets
          </ActionButton>
          
          {savedSubtitlePresets.length > 0 && (
            <ActionButton onClick={() => {
              const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(savedSubtitlePresets, null, 2));
              const downloadAnchorNode = document.createElement('a');
              downloadAnchorNode.setAttribute("href", dataStr);
              downloadAnchorNode.setAttribute("download", "subtitle_presets.json");
              document.body.appendChild(downloadAnchorNode);
              downloadAnchorNode.click();
              downloadAnchorNode.remove();
            }}>
              <Download size={14} />
              Export Presets
            </ActionButton>
          )}
        </PresetActions>

        {savedSubtitlePresets.length > 0 && (
          <SavedPresetsList>
            {savedSubtitlePresets.map((preset) => (
              <PresetItem key={preset.id}>
                <div>
                  <PresetName>
                    <Globe size={12} style={{ marginRight: '6px' }} />
                    {preset.name}
                  </PresetName>
                  <PresetInfo>
                    📝 {preset.maxWordsPerLine} words/line • 📏 {preset.fontSize}px • 📍 {preset.position}
                  </PresetInfo>
                </div>
                <PresetActions2>
                  <SmallButton
                    onClick={() => loadSubtitlePreset(preset)}
                    title="Apply this preset"
                  >
                    <Copy size={12} />
                  </SmallButton>
                  <SmallButton
                    onClick={() => deleteSubtitlePreset(preset.id)}
                    title="Delete preset"
                  >
                    <X size={12} />
                  </SmallButton>
                </PresetActions2>
              </PresetItem>
            ))}
          </SavedPresetsList>
        )}
      </Section>

      {/* Hidden file inputs */}
      <input
        id="subtitlePresetUpload"
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
              try {
                const imported = JSON.parse(event.target.result);
                setSavedSubtitlePresets([...savedSubtitlePresets, ...imported]);
                localStorage.setItem('subtitlePresets', JSON.stringify([...savedSubtitlePresets, ...imported]));
              } catch (error) {
                console.error('Error importing subtitle presets:', error);
              }
            };
            reader.readAsText(file);
          }
        }}
      />

      {/* Save Preset Modal */}
      {showSaveModal && (
        <Modal onClick={() => setShowSaveModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Save Subtitle Preset</ModalTitle>
            
            <ModalFormGroup>
              <ModalLabel>Preset Name</ModalLabel>
              <ModalInput
                type="text"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="Enter preset name..."
                autoFocus
              />
            </ModalFormGroup>

            <ModalActions>
              <ActionButton onClick={() => setShowSaveModal(false)}>
                Cancel
              </ActionButton>
              <ActionButton
                variant="primary"
                onClick={saveSubtitlePreset}
                disabled={!presetName.trim()}
              >
                <Save size={14} />
                Save Preset
              </ActionButton>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default SubtitleSettings; 